import AssignmentAttachments from './AssignmentAttachments';
import AssignmentComments from './AssignmentComments';
import React from 'react';
import EntityMultiSelect, { selectedEntityIds, entityLabel } from '../../../../shared/components/EntityMultiSelect';
import { PartyFormSection } from '../../../../shared/components/PartyFormFields';
import { assignmentFormSchema } from '../../config/assignmentForm.schema';
import { Plus } from '../../../../shared/assets/AppIcons';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveDealsCustomField,
  createDealsCustomField,
  listDealsCustomFields,
} from '../../services/deals-custom-fields.service';
import {
  CreateFormCancelButton,
  CreateFormDialog,
  CreateFormSubmitButton,
} from '../../../../shared/components/CreateFormDialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import AssignmentAttachmentPicker from './AssignmentAttachmentPicker';
import '../../styles/task-form.css';

const ASSIGNMENT_CUSTOM_FIELDS_API = {
  list: listDealsCustomFields,
  create: createDealsCustomField,
  archive: archiveDealsCustomField,
};
const PRIORITIES = [{ value: 'bassa', label: 'Bassa' }, { value: 'media', label: 'Media' }, { value: 'alta', label: 'Alta' }];

function FieldError({ id, children }) {
  return children ? <p id={id} role="alert" className="task-form__error wt-inline-error">{children}</p> : null;
}

export default function CreateAssignmentDialog({
  open,
  onOpenChange,
  mode = 'create',
  staff = [],
  staffCatalogStatus = 'empty',
  clients = [],
  clientsCatalogStatus = 'empty',
  suppliers = [],
  suppliersCatalogStatus = 'empty',
  creationWarning = '',
  assignmentTypes = [],
  newAssignment,
  formErrors = {},
  onFieldChange,
  onSubmit,
  isSubmitting = false,
  assignment,
  currentUser,
}) {
  const isEditMode = mode === 'edit';
  const selectableRecipients = staff.filter((member) => member.linkedWorkspaceUserId);
  const recipientsDisabled = selectableRecipients.length === 0;
  const recipientsMessage = staffCatalogStatus === 'unavailable'
    ? 'Catalogo staff non disponibile. Destinatari momentaneamente non selezionabili.'
    : 'Nessun membro del personale disponibile da assegnare.';
  const activeSuppliers = suppliers.filter((supplier) => supplier.status !== 'INACTIVE');
  const clientsUnavailable = ['unavailable', 'auth_required'].includes(clientsCatalogStatus);
  const suppliersUnavailable = ['unavailable', 'auth_required'].includes(suppliersCatalogStatus);
  const clientsMessage = clientsCatalogStatus === 'unavailable'
    ? 'Catalogo clienti non disponibile. Puoi creare un task generale senza cliente.'
    : clientsCatalogStatus === 'auth_required'
      ? 'Sessione non autorizzata per il catalogo clienti. Puoi creare un task generale.'
      : 'Nessun cliente disponibile nel catalogo. Il task resterà generale.';
  const suppliersMessage = suppliersCatalogStatus === 'unavailable'
    ? 'Catalogo fornitori non disponibile. Puoi creare un task senza collegamento.'
    : suppliersCatalogStatus === 'auth_required'
      ? 'Sessione non autorizzata per il catalogo fornitori.'
      : 'Nessun fornitore attivo disponibile.';
  const linkType = newAssignment.linkType ||
    (selectedEntityIds(newAssignment, 'supplier').length ? 'supplier' : selectedEntityIds(newAssignment, 'client').length ? 'client' : 'none');

  function handleLinkTypeChange(value) {
    onFieldChange('linkType', value);
    if (value !== 'client') {
      onFieldChange('clientIds', []);
      onFieldChange('clientId', '');
    }
    if (value !== 'supplier') {
      onFieldChange('supplierIds', []);
      onFieldChange('supplierId', '');
      onFieldChange('supplierName', '');
    }
  }

  return (
    <CreateFormDialog
      busy={isSubmitting}
      open={open}
      onOpenChange={onOpenChange}
      icon={Plus}
      title={isEditMode ? 'Modifica task' : 'Nuovo task'}
      className="party-form-dialog task-form-dialog"
      footer={
        <>
          <CreateFormCancelButton onClick={() => onOpenChange(false)} disabled={isSubmitting} />
          <CreateFormSubmitButton saving={isSubmitting} form="assignment-form" disabled={recipientsDisabled && !isEditMode}>
            {isEditMode ? 'Salva modifiche' : 'Crea task'}
          </CreateFormSubmitButton>
        </>
      }
    >
      <form id="assignment-form" className="party-form task-form" onSubmit={(event) => {
        event.preventDefault();
        if (!isSubmitting) onSubmit();
      }}>
        <div className="party-form__field" data-form-field="title">
          <Label htmlFor="title">Titolo *</Label>
          <Input id="title" required disabled={isSubmitting}
            aria-invalid={Boolean(formErrors.title)} aria-describedby={formErrors.title ? 'assignment-title-error' : undefined}
            placeholder="Esempio: Implementare login con OAuth2" value={newAssignment.title}
            onChange={(event) => onFieldChange('title', event.target.value)} />
          <FieldError id="assignment-title-error">{formErrors.title}</FieldError>
        </div>

        <div className="party-form__field" data-form-field="description">
          <Label htmlFor="description">Descrizione *</Label>
          <Textarea id="description" required disabled={isSubmitting} rows={3}
            aria-invalid={Boolean(formErrors.description)} aria-describedby={formErrors.description ? 'assignment-description-error' : undefined}
            placeholder="Descrivi dettagliatamente l’obiettivo del task, i requisiti minimi e i criteri di accettazione…"
            value={newAssignment.description} onChange={(event) => onFieldChange('description', event.target.value)} />
          <FieldError id="assignment-description-error">{formErrors.description}</FieldError>
        </div>

        <fieldset className="party-form__segments" disabled={isSubmitting}>
          <legend>Priorità</legend>
          <div className="party-form__segment-options">
            {PRIORITIES.map(({ value, label }) => <label key={value}>
              <input type="radio" name="assignment-priority" value={value} checked={newAssignment.urgency === value}
                onChange={() => onFieldChange('urgency', value)} />
              <span>{label}</span>
            </label>)}
          </div>
        </fieldset>

        {creationWarning ? <div role="alert" className="assignment-form-dialog__warning wt-inline-error">{creationWarning}</div> : null}

        <PartyFormSection title="Classificazione">
          <div className="party-form__row">
            <div className="party-form__field">
              <Label htmlFor="type">{assignmentFormSchema.field('type').label}</Label>
              <Select value={newAssignment.type} onValueChange={(value) => onFieldChange('type', value)} disabled={isSubmitting}>
                <SelectTrigger id="type" aria-required="true"><SelectValue placeholder="Seleziona tipo…" /></SelectTrigger>
                <SelectContent>{assignmentTypes.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="party-form__field">
              <Label htmlFor="dueDate">Scadenza (opzionale)</Label>
              <Input id="dueDate" type="date" disabled={isSubmitting} value={newAssignment.dueDate}
                aria-invalid={Boolean(formErrors.dueDate)} aria-describedby={formErrors.dueDate ? 'assignment-due-date-error' : undefined}
                onChange={(event) => onFieldChange('dueDate', event.target.value)} />
              <FieldError id="assignment-due-date-error">{formErrors.dueDate}</FieldError>
            </div>
          </div>
        </PartyFormSection>

        <PartyFormSection title="Entità collegate">
          <div className="party-form__row">
            <div className="party-form__field">
              <Label htmlFor="assignment-recipients">Assegnato a *</Label>
              <EntityMultiSelect id="assignment-recipients" label="Assegnato a" required
                disabled={isSubmitting || staff.length === 0}
                options={staff.map((member) => ({
                  id: member.linkedWorkspaceUserId ? String(member.linkedWorkspaceUserId) : `staff:${member.id}`,
                  inputId: `recipient-${member.id}`, name: member.name,
                  disabled: !member.linkedWorkspaceUserId,
                  description: member.linkedWorkspaceUserId ? 'Con accesso gestionale' : 'Esterno - senza accesso gestionale. Non assegnabile come destinatario task finché non ha un account.',
                }))}
                value={newAssignment.recipientIds} onChange={(ids) => onFieldChange('recipientIds', ids)} />
              <FieldError>{recipientsDisabled ? recipientsMessage : formErrors.recipientIds}</FieldError>
            </div>
            <div className="party-form__field">
              <Label htmlFor="linkType">Tipo entità</Label>
              <Select value={linkType} onValueChange={handleLinkTypeChange} disabled={isSubmitting}>
                <SelectTrigger id="linkType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna entità</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="supplier">Fornitore</SelectItem>
                </SelectContent>
              </Select>
              <FieldError>{formErrors.linkType}</FieldError>
            </div>
          </div>

          {linkType === 'client' ? <div className="party-form__field">
            <Label htmlFor="assignment-clients">Clienti <span className="sr-only">Cliente collegato</span></Label>
            <EntityMultiSelect id="assignment-clients" label="Clienti" options={clients} disabled={isSubmitting || clientsUnavailable}
              value={selectedEntityIds(newAssignment, 'client')}
              onChange={(ids) => { onFieldChange('clientIds', ids); onFieldChange('clientId', ids[0] || ''); }} />
            {clientsUnavailable || clients.length === 0
              ? <FieldError>{clientsMessage}</FieldError>
              : <p className="party-form__help">Puoi collegare più clienti al task.</p>}
          </div> : null}

          {linkType === 'supplier' ? <div className="party-form__field">
            <Label htmlFor="assignment-suppliers">Fornitori <span className="sr-only">Fornitore collegato</span></Label>
            <EntityMultiSelect id="assignment-suppliers" label="Fornitori" options={activeSuppliers} disabled={isSubmitting || suppliersUnavailable}
              value={selectedEntityIds(newAssignment, 'supplier')}
              onChange={(ids) => {
                onFieldChange('supplierIds', ids);
                onFieldChange('supplierId', ids[0] || '');
                onFieldChange('supplierName', ids.map((id) => entityLabel(activeSuppliers.find((item) => String(item.id) === String(id)))).join(', '));
              }} />
            {suppliersUnavailable || activeSuppliers.length === 0
              ? <FieldError>{suppliersMessage}</FieldError>
              : <p className="party-form__help">Puoi collegare più fornitori al task.</p>}
            <FieldError>{formErrors.supplierId}</FieldError>
          </div> : null}

          {isEditMode ? <AssignmentAttachments formLayout assignment={assignment || newAssignment} currentUser={currentUser} /> : <AssignmentAttachmentPicker files={newAssignment.attachments || []}
            onChange={(files) => onFieldChange('attachments', files)} disabled={isSubmitting} />}
        </PartyFormSection>

        <ClientCustomFieldsSection formKey={assignmentFormSchema.id} values={newAssignment.customFields || {}}
          onChange={(customFields) => onFieldChange('customFields', customFields)} api={ASSIGNMENT_CUSTOM_FIELDS_API} />
      </form>
      {isEditMode && open && (assignment?.id || newAssignment.id) && <AssignmentComments
        assignment={assignment || newAssignment} currentUser={currentUser} staff={staff} />}
    </CreateFormDialog>
  );
}
